'use client'

import { useEffect, useState } from 'react'
import { fulfillmentApi, type FulfillmentApi } from '@entities/fulfillment'

/** Запрос, к которому относится результат: ответ на устаревший запрос показывать нельзя. */
type SearchState = {
  status: 'idle' | 'success' | 'error'
  query: string
}

/** Пауза перед запросом, чтобы не дёргать сервер на каждую букву. */
const searchDelayMilliseconds = 250

export const cityOptionLabel = (option: FulfillmentApi.ServiceCity) => `${option.name}, ${option.region_name}`

/**
 * Порядок подсказок: сначала города, чьё название начинается с запроса, затем те, где запрос
 * встретился внутри названия, и только потом найденные по названию области. Иначе на запрос
 * «Воронеж» первыми идут Бобров и Богучар — они попали в ответ из-за Воронежской области.
 */
const matchRank = (option: FulfillmentApi.ServiceCity, query: string) => {
  const name = option.name.toLocaleLowerCase('ru')
  if (name.startsWith(query)) {
    return 0
  }

  return name.includes(query) ? 1 : 2
}

/**
 * Сортировка массива устойчивая, поэтому внутри одной группы сохраняется порядок сервера: он знает
 * про значимость города больше нас.
 */
const sortByMatch = (options: FulfillmentApi.ServiceCity[], query: string) => {
  const normalizedQuery = query.toLocaleLowerCase('ru')

  return [...options].sort((left, right) => matchRank(left, normalizedQuery) - matchRank(right, normalizedQuery))
}

/**
 * Подсказки городов из справочника: поиск с паузой, состояние списка и текст ошибки запроса.
 * Общий для выбора складов и городов обслуживания.
 */
export const useCitySearch = () => {
  const [query, setQuery] = useState('')
  const [options, setOptions] = useState<FulfillmentApi.ServiceCity[]>([])
  const [isListOpen, setIsListOpen] = useState(false)
  const [searchState, setSearchState] = useState<SearchState>({ status: 'idle', query: '' })
  const normalizedQuery = query.trim()

  useEffect(() => {
    if (!normalizedQuery) {
      return
    }
    let active = true
    const timer = window.setTimeout(() => {
      fulfillmentApi
        .serviceCityOptions({ q: normalizedQuery, limit: 20 })
        .then((response) => {
          if (!active) {
            return
          }
          setOptions(sortByMatch(response.data.data?.cities ?? [], normalizedQuery))
          setSearchState({ status: 'success', query: normalizedQuery })
        })
        .catch(() => {
          if (!active) {
            return
          }
          setOptions([])
          setSearchState({ status: 'error', query: normalizedQuery })
        })
    }, searchDelayMilliseconds)
    return () => {
      active = false
      window.clearTimeout(timer)
    }
  }, [query])

  const isEmptySearchResult =
    searchState.status === 'success' &&
    searchState.query === normalizedQuery &&
    options.length === 0 &&
    Boolean(normalizedQuery)

  const reset = () => {
    setQuery('')
    setOptions([])
    setIsListOpen(false)
    setSearchState({ status: 'idle', query: '' })
  }

  const changeQuery = (value: string) => {
    setQuery(value)
    setOptions([])
    setIsListOpen(Boolean(value.trim()))
    setSearchState({ status: 'idle', query: value.trim() })
  }

  return {
    query,
    normalizedQuery,
    options,
    changeQuery,
    reset,
    setIsListOpen,
    isPopupOpen: isListOpen && (options.length > 0 || isEmptySearchResult),
    requestError:
      searchState.status === 'error' && searchState.query === normalizedQuery
        ? 'Не удалось загрузить города. Введите город и регион вручную.'
        : '',
  }
}
